// Mobilnummer til og fra Vipps. Vipps vil ha MSISDN: landskode og nummer, bare sifre, uten «+» og «00»
// (ePayment-API-et, customer.phoneNumber: ^\d{9,15}$, f.eks. 4791234567). Vipps MobilePay har brukere i hele
// Norden, og salgsstedet må kunne ta imot kunder fra andre land (Vipps' sjekkliste, «Handle cross-border
// payments»). Samme regler som ElseWeb (packages/shared/src/vipps.ts) og Elsemobil.
//
// Fila finnes to steder i Pay, likt: backend/src/lib/telefon.ts og frontend/src/lib/telefon.ts. Backend kan
// ikke laste kode fra @pay/shared (bare typer der), og test/telefon.test.ts sjekker at kopiene er like.

/** To-sifrede landskoder (ITU-T E.164). 1 og 7 er ett siffer, alle andre tre. Kodene er prefiksfrie. */
const LANDSKODER_TO = new Set([
  '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84',
  '86', '90', '91', '92', '93', '94', '95', '98',
]);

const LAND: Record<string, string> = {
  '47': 'Norge', '45': 'Danmark', '46': 'Sverige', '358': 'Finland', '354': 'Island', '298': 'Færøyene',
  '299': 'Grønland', '48': 'Polen', '49': 'Tyskland', '44': 'Storbritannia', '370': 'Litauen', '371': 'Latvia',
  '372': 'Estland', '31': 'Nederland', '32': 'Belgia', '33': 'Frankrike', '34': 'Spania', '39': 'Italia',
  '351': 'Portugal', '353': 'Irland', '41': 'Sveits', '43': 'Østerrike', '420': 'Tsjekkia', '421': 'Slovakia',
  '36': 'Ungarn', '40': 'Romania', '359': 'Bulgaria', '385': 'Kroatia', '380': 'Ukraina', '30': 'Hellas',
  '90': 'Tyrkia', '1': 'USA/Canada',
};

/**
 * Mobilnummeret slik det er skrevet → MSISDN, eller null når Vipps ikke kan bruke det.
 * - Åtte siffer som begynner på 4 eller 9, er et norsk mobilnummer: 47 settes foran.
 * - Med «+» eller «00» foran, eller med ti siffer og mer, står landskoden i nummeret. Alle land godtas.
 * - Et norsk nummer (47) må være et mobilnummer (4… eller 9…).
 * - Alt annet er ugyldig, også åtte siffer som begynner på noe annet: norsk fasttelefon, eller et
 *   utenlandsk nummer uten landskode (dansk 20 12 34 56 må skrives +45 20 12 34 56).
 */
export function tilMsisdn(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim().replace(/[\s().-]/g, '');
  if (!s) return null;
  const medLandskode = s.startsWith('+') || s.startsWith('00');
  const d = s.startsWith('+') ? s.slice(1) : s.startsWith('00') ? s.slice(2) : s;
  if (!/^\d+$/.test(d)) return null;
  if (!medLandskode && /^[49]\d{7}$/.test(d)) return `47${d}`;
  if (d.startsWith('47')) return /^47[49]\d{7}$/.test(d) ? d : null;
  if (d.startsWith('0')) return null;
  return d.length >= (medLandskode ? 9 : 10) && d.length <= 15 ? d : null;
}

/** Landskoden i et MSISDN (1–3 sifre). */
function landskode(msisdn: string): string {
  if (/^[17]/.test(msisdn)) return msisdn.slice(0, 1);
  return LANDSKODER_TO.has(msisdn.slice(0, 2)) ? msisdn.slice(0, 2) : msisdn.slice(0, 3);
}

/**
 * Et lagret nummer (MSISDN fra kravet, eller nummeret slik det er skrevet) → MSISDN. Et MSISDN kan ha ni
 * sifre (+298 123456), og eldre krav kan ha åtte siffer uten landskode.
 */
export function somMsisdn(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim();
  return tilMsisdn(s) ?? (/^\d{9}$/.test(s) ? tilMsisdn(`+${s}`) : null);
}

/** Landet nummeret hører til, når vi har navnet: «Norge», «Danmark» … */
export function mobilLand(raw: string | null | undefined): string | null {
  const m = somMsisdn(raw);
  return m ? (LAND[landskode(m)] ?? null) : null;
}

/**
 * Visningsform med landskode, så det synes hvor kravet går: 4790605573 → «+47 906 05 573»,
 * 4520123456 → «+45 20 12 34 56». Et nummer som ikke kan tolkes, vises som det er.
 */
export function visMobil(raw: string | null | undefined): string {
  const m = somMsisdn(raw);
  if (!m) return (raw ?? '').trim();
  const kode = landskode(m);
  const n = m.slice(kode.length);
  let grupper: string;
  if (kode === '47' && n.length === 8) grupper = `${n.slice(0, 3)} ${n.slice(3, 5)} ${n.slice(5)}`;
  else if (kode === '45' && n.length === 8) grupper = `${n.slice(0, 2)} ${n.slice(2, 4)} ${n.slice(4, 6)} ${n.slice(6)}`;
  else if (kode === '46' && n.length === 9) grupper = `${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5, 7)} ${n.slice(7)}`;
  else if (kode === '358' && n.length >= 8) grupper = `${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`;
  else {
    const g = n.match(/\d{1,3}/g) ?? [];
    if (g.length > 1 && g[g.length - 1]!.length === 1) g.splice(-2, 2, g[g.length - 2]! + g[g.length - 1]!);
    grupper = g.join(' ');
  }
  return `+${kode} ${grupper}`;
}

/** Åtte siffer uten landskode: tolket som norsk nummer. Skjermen ber da om landskode for utenlandske numre. */
export function mobilAntattNorsk(raw: string | null | undefined): boolean {
  return /^[49]\d{7}$/.test((raw ?? '').trim().replace(/[\s().-]/g, ''));
}
